<template>
  <b-card
    no-body
    class="change-password-card"
  >
    <validation-observer ref="simpleRules">
      <b-card-body class="change-password-body">
        <b-form
          ref="myForm"
          @submit.prevent="handleForm"
        >
          <h3 class="change-password-title">
            Change Password
          </h3>

          <b-row>
            <b-col cols="12">
              <validation-provider
                #default="{ errors }"
                rules="required"
                name="Current Password"
              >
                <b-form-group class="mb-1">
                  <label
                    for="current-password"
                    class="password-label"
                  >Current Password</label>
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="current-password"
                      v-model="passwordValueOld"
                      name="old-password"
                      :type="passwordFieldTypeOld"
                      placeholder="Current Password"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer password-eye"
                        :icon="passwordToggleIconOld"
                        @click="togglePasswordOld"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                vid="new_password"
                rules="required"
                name="New Password"
              >
                <b-form-group class="mb-1">
                  <label
                    for="account-new-password"
                    class="password-label"
                  >New Password</label>
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="account-new-password"
                      v-model="newPasswordValue"
                      :type="passwordFieldTypeNew"
                      name="new-password"
                      placeholder="New Password"
                      @input="handlePasswordChange"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer password-eye"
                        :icon="passwordToggleIconNew"
                        @click="togglePasswordNew"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                vid="confirm_password"
                rules="required|confirmed:new_password"
                name="confirm_password"
                :custom-messages="confirmPasswordMessages"
              >
                <b-form-group class="mb-1">
                  <label
                    for="account-retype-new-password"
                    class="password-label"
                  >Confirm New Password</label>
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="account-retype-new-password"
                      v-model="RetypePassword"
                      :type="passwordFieldTypeRetype"
                      name="retype-password"
                      placeholder="Confirm New Password"
                      @input="onConfirmPasswordInput"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer password-eye"
                        :icon="passwordToggleIconRetype"
                        @click="togglePasswordRetype"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ confirmPasswordError || errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <h4 class="password-req-title">
                Password Requirements:
              </h4>
              <ul class="password-req-list">
                <li :class="{ 'text-success': isPasswordLengthValid }">
                  Minimum 8 characters long - the more, the better
                </li>
                <li :class="{ 'text-success': isLowerCaseValid }">
                  At least one lowercase character
                </li>
                <li :class="{ 'text-success': isSpecialCharacterValid }">
                  At least one number, symbol, or whitespace character
                </li>
              </ul>
            </b-col>

            <b-col cols="12">
              <div class="change-password-actions">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mr-1 change-password-save-btn"
                  :disabled="isChangingPassword"
                  @click="validatePasswordFields"
                >
                  <b-spinner v-if="isChangingPassword" small class="mr-50" />
                  Save Changes
                </b-button>
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="reset"
                  variant="outline-secondary"
                >
                  Cancel
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </b-card-body>
    </validation-observer>
  </b-card>
</template>

<script>
import {
  BButton, BForm, BFormGroup, BFormInput, BRow, BCol, BCard, BCardBody, BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import {
  required, confirmed,
} from '@validations'

export default {
  components: {
    BButton,
    ValidationProvider,
    ValidationObserver,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BInputGroup,
    BInputGroupAppend,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      accessToken: localStorage.getItem('accessToken'),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      required,
      passwordValueOld: '',
      newPasswordValue: '',
      RetypePassword: '',
      passwordFieldTypeOld: 'password',
      passwordFieldTypeNew: 'password',
      passwordFieldTypeRetype: 'password',
      isPasswordLengthValid: false,
      isLowerCaseValid: false,
      isSpecialCharacterValid: false,
      isChangingPassword: false,
      confirmPasswordError: '',
      confirmPasswordMessages: {
        confirmed: 'New password and confirm password do not match.',
        required: 'Confirm new password is required.',
      },
    }
  },
  computed: {
    isPasswordValid() {
      return (
        this.isPasswordLengthValid
        && this.isLowerCaseValid
        && this.isSpecialCharacterValid
      )
    },
    passwordToggleIconOld() {
      return this.passwordFieldTypeOld === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    passwordToggleIconNew() {
      return this.passwordFieldTypeNew === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    passwordToggleIconRetype() {
      return this.passwordFieldTypeRetype === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    passwordsMatch() {
      return String(this.newPasswordValue) === String(this.RetypePassword)
    },
    onConfirmPasswordInput() {
      this.confirmPasswordError = ''
    },
    async validatePasswordFields() {
      this.confirmPasswordError = ''

      if (!this.passwordsMatch()) {
        this.confirmPasswordError = 'New password and confirm password do not match.'
        await this.$refs.simpleRules.validate()
        return
      }

      const valid = await this.$refs.simpleRules.validate()
      if (!valid) return

      if (!this.isPasswordValid) {
        this.$swal({
          title: 'Warning',
          timer: 5000,
          text: 'Password does not meet the requirements',
          icon: 'warning',
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
        return
      }

      if (!this.passwordsMatch()) {
        this.confirmPasswordError = 'New password and confirm password do not match.'
        return
      }

      this.handleForm()
    },
    handleForm() {
      if (this.isChangingPassword) return
      if (!this.passwordsMatch()) {
        this.confirmPasswordError = 'New password and confirm password do not match.'
        return
      }
      this.isChangingPassword = true
      const customerData = {
        old_password: this.passwordValueOld,
        new_password: this.newPasswordValue,
      }
      vendorPanelApi.changePassword(customerData)
        .then(response => {
          const successText = response.data.message || response.data.success || 'Password Updated Successfully'
          this.$swal({
            title: 'Submitted',
            text: successText,
            icon: 'success',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
          this.$refs.myForm.reset()
          this.passwordValueOld = ''
          this.newPasswordValue = ''
          this.RetypePassword = ''
          this.validatePasswordRequirements()
          this.$forceUpdate()
        })
        .catch(error => {
          const message = error.response?.data?.message || `${error}`
          this.$swal({
            title: 'Error!',
            text: message,
            icon: 'error',
            timer: 5000,
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        })
        .finally(() => {
          this.isChangingPassword = false
        })
    },
    validatePasswordRequirements() {
      this.isPasswordLengthValid = this.newPasswordValue.length >= 8
      this.isLowerCaseValid = /[a-z]/.test(this.newPasswordValue)
      this.isSpecialCharacterValid = /[0-9!@#$%^&*(),.?":{}|<>\s]/.test(this.newPasswordValue)
    },
    handlePasswordChange() {
      this.confirmPasswordError = ''
      this.validatePasswordRequirements()
    },
    togglePasswordOld() {
      this.passwordFieldTypeOld = this.passwordFieldTypeOld === 'password' ? 'text' : 'password'
    },
    togglePasswordNew() {
      this.passwordFieldTypeNew = this.passwordFieldTypeNew === 'password' ? 'text' : 'password'
    },
    togglePasswordRetype() {
      this.passwordFieldTypeRetype = this.passwordFieldTypeRetype === 'password' ? 'text' : 'password'
    },
  },
}
</script>

<style scoped lang="scss">
.change-password-card {
  border: 1px solid #f1f1f6;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(39, 44, 62, 0.05);
}

.change-password-body {
  padding: 1rem;

  @media (min-width: 992px) {
    padding: 1.25rem 1.1rem 1.15rem;
  }
}

.change-password-title {
  font-weight: 700;
  font-size: 1.38rem;
  color: #3f3b57;
  margin-bottom: 1rem;
}

.password-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: #5b5670;
  margin-bottom: 0.35rem;
}

.password-eye {
  color: #7f7a93;
  stroke-width: 2.2;
}

::v-deep .input-group-append .input-group-text {
  background: #fff;
  border-left: 0;
}

::v-deep .form-control {
  border-color: #ececf3;
  min-height: 2.35rem;
  font-size: 0.9rem;
}

.password-req-title {
  font-weight: 700;
  font-size: 1rem;
  color: #55526a;
  margin-top: 0.35rem;
  margin-bottom: 0.45rem;
}

.password-req-list {
  margin: 0 0 0.65rem 1.15rem;
  padding: 0;
  color: #6f6b86;
  font-size: 0.95rem;
  line-height: 1.5;
}

.change-password-actions {
  margin-top: 0.35rem;
}

.change-password-save-btn {
  background-color: #082748 !important;
  border-color: #082748 !important;
  color: #fff !important;
  box-shadow: 0 2px 8px rgba(8, 39, 72, 0.24);

  &:hover,
  &:focus,
  &:active {
    background-color: #06213c !important;
    border-color: #06213c !important;
    color: #fff !important;
  }
}
</style>
